import _                                          from "underscore";
import React, { FC, useEffect, useRef, useState } from 'react';
import { Slider }                                 from 'react-semantic-ui-range';
import { useField, useFormikContext }             from 'formik';
import { FormFieldProps }                         from 'semantic-ui-react';

import { FieldWrapper }              from './FieldWrapper';
import { FieldDescription }          from './FieldDescription';
import { StandaloneFormikMetaProps } from '@src/types';
import { INPUT }                     from "@src/constants";


interface RangeFieldProps {
    label?: string,
    descriptionText?: string,
    includeFieldWrapper?: boolean,
    includeErrorMessage?: boolean,
    debounce?: boolean,
    debounceValue?: number | number[],
    valuePreprocessor?: ( value: number | number[] ) => number | number[],
    debounceTimingMS?: number,
    onDebounceChange?: ( value: number | number[] ) => void,
    onRangeChange?: ( value: number | number[] ) => void,

    [ additionalProps: string ]: any
}

const defaultProps: RangeFieldProps = {
    label:               null,
    descriptionText:     null,
    includeFieldWrapper: true,
    includeErrorMessage: true,
    debounce:            false,
    debounceValue:       0,
    valuePreprocessor:   null,
    debounceTimingMS:    INPUT.INPUT_EVENT_DELAY,
    onDebounceChange:    ( value ) => {},
    onRangeChange:       ( value ) => {},
}


// preprocess a value...
const preprocessValue = ( value: any, method: ( v: any ) => any | null ) => {

    return ( _.isFunction( method ) ) ? method( value ) : value;
}

const RangeField: FC<RangeFieldProps> = ( _props ) => {

    let {
            label,
            descriptionText,
            includeFieldWrapper,
            includeErrorMessage,
            debounce,
            debounceValue,
            ...props
        } = _props;

    // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
    // which we can spread on <input> and also replace ErrorMessage entirely.
    let formik                      = useFormikContext(),
        valueRef                    = useRef( preprocessValue( debounceValue, _props.valuePreprocessor ) ),
        [ timeoutID, setTimeoutID ] = useState<NodeJS.Timeout>( null );

    // clean up any debounced timers
    useEffect( () => {

        return () => {

            if( timeoutID ) clearTimeout( timeoutID );
        }
    }, [] )

    // if the debounced value changes we can reset our ref to that value
    useEffect( () => {

        valueRef.current = debounceValue;

    }, [ debounceValue ] )


    const [ field, meta ] = ( formik ? useField( props.name ) : [ { name: props.name }, {} ] as [ FormFieldProps, StandaloneFormikMetaProps ] );

    let showErrors  = ( meta.touched && meta.error ),
        Wrapper: FC = includeFieldWrapper ? FieldWrapper : React.Fragment;

    // merge input props, html props, and field props
    let sliderProps = {
        ...field,
        ...props,
    }

    // if behaving like a debounced input...
    if( debounce ){

        // make the input value the currently typed in value from ref
        sliderProps[ 'value' ] = valueRef.current;

        // make on change behave like a debounced input.
        // NOTE: debounce change behavior needs to be updated manually.
        // Adapted from https://stackoverflow.com/a/57039707
        sliderProps[ 'settings' ] = { ...( sliderProps[ 'settings' ] ?? { onChange: null } ) };
        sliderProps[ 'settings' ][ 'onChange' ] = ( value: number | number[] ) => {

            // save the currently selected value
            valueRef.current = preprocessValue( value, _props.valuePreprocessor );

            // clear any timer running
            if( timeoutID ) clearTimeout( timeoutID );

            _props.onRangeChange( valueRef.current );

            // set new timer to perform the change after `debounceTimingMS` milliseconds
            setTimeoutID( setTimeout( () => {

                _props.onDebounceChange( valueRef.current );

            }, _props.debounceTimingMS ) );
        }

    } else {

        sliderProps[ 'value' ] = preprocessValue( sliderProps[ 'value' ], _props.valuePreprocessor );

    }

    return (

        <Wrapper { ...{ showErrors, includeErrorMessage, meta, field } }>

            { label && <label htmlFor={ props.id || props.name }>{ label }</label> }

            { descriptionText && <FieldDescription>{ descriptionText }</FieldDescription> }

            <Slider { ...sliderProps } />

        </Wrapper>
    );
};

RangeField.defaultProps = defaultProps;

export { RangeField };
